/**
 * `list-extraction-fields` and `change-extraction-field` — CLM-109 (FR-33,
 * DR-09, NFR-17, CR-07). Refinements R-109-1 … R-109-15 are proposals made
 * while designing; D-2.1-7, which the gate leans on, is a recommendation, not a
 * ratified decision.
 *
 * An admin defines extracted fields without a code change or a deployment:
 * each is a key, a description the model reads and a value bound, and the next
 * extraction returns it as a `field_verification` row under
 * `additional_terms.<field_key>`. The fields live in `clm.extraction_field` —
 * append-only, one row per change, a global gapless revision — so the set any
 * extraction ran under is a query, not a replay (DR-09).
 *
 * ## A list entry, never a top-level property (FR-33)
 *
 * CLM-80's ceiling leaves two of Anthropic's sixteen union slots, held for
 * governed fields; an admin field is an entry in a fixed `additional_terms`
 * list, which costs none. `placement: 'top_level'` exists only so that the
 * request FR-33 says must be refused *is* refused, explicitly and naming
 * CLM-80 (`top_level_refused`), rather than as a generic unknown-key 400. No
 * value of it adds a property to anything.
 *
 * ## Refused at save, naming the cap (NFR-17)
 *
 * A change is applied to a copy of the current set and checked before it is
 * saved: the field-count cap, the value bound, the description bounds, rule
 * 13's and CR-03's tripwires, a known party name (R-109-10) — each over the
 * description **and** the key, which reaches the model in clear too — and —
 * though no admin input can move them — the output format's union
 * count, optional-parameter count and size. Each refusal is a `field_errors`
 * entry whose message is `code: sentence`, and the sentence names the cap.
 *
 * ## One change per revision, and two admins at once (R-109-14)
 *
 * A change carries `expected_revision` — the revision the caller saw. The
 * insert writes `expected_revision + 1` only if that is still the next number,
 * and `uq_extraction_field_revision` refuses the loser of a race: both are
 * `409 field_set_changed`, and the page re-reads.
 *
 * ## Gate (R-109-14, D-2.1-7)
 *
 * `admin:configure` on both — Admin only, no Legal co-sign. The list is
 * org-wide configuration, read unscoped like the `cloudeq_entity` registry
 * (rule 9 is about contract rows; there are none here).
 *
 * Shared-types cannot depend on `@clm/extraction`, which owns the rules, so the
 * request here is deliberately **loose** — a key of any shape, a description of
 * up to 2,000 characters, any positive bound — and the route's checks produce
 * the named refusals. A tight schema here would turn `value_max: 401` into a
 * generic validation error that names no cap.
 */
import { z } from 'zod';
import { FieldNameSchema } from '../domain/verification.js';
import { IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema } from '../primitives.js';
/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */
/** `clm.extraction_field.change_kind`. A rename is a deactivate plus an add (R-109-2). */
export const EXTRACTION_FIELD_CHANGE_KINDS = ['add', 'edit', 'deactivate', 'reactivate'];
export const ExtractionFieldChangeKindSchema = z.enum(EXTRACTION_FIELD_CHANGE_KINDS);
/** Where a field would be added. Only `list_entry` is ever saved (FR-33). */
export const EXTRACTION_FIELD_PLACEMENTS = ['list_entry', 'top_level'];
/**
 * The key as the registry stores it: one snake_case segment, at most 48
 * characters, starting with a letter — `ck_extraction_field_key` and
 * `EXTRACTION_FIELD_KEY_PATTERN` in `@clm/extraction`, whose test pins the
 * two equal. Used on responses; requests are loose (see the module note).
 */
export const EXTRACTION_FIELD_KEY_WIRE_PATTERN = /^[a-z][a-z0-9_]{0,47}$/;
export const ExtractionFieldKeyWireSchema = z.string().regex(EXTRACTION_FIELD_KEY_WIRE_PATTERN, 'expected an admin field key');
/** `ck_extraction_field_change_note`: 1..500, never blank. */
export const ExtractionFieldNoteSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((value) => value.trim().length > 0, 'Must contain a non-whitespace character');
/** Revisions: `0` is the governed set alone; every change is the next one. */
const Revision = z.number().int().min(0);
/* -------------------------------------------------------------------------- */
/* Refusal codes — each a `field_errors` message prefix                        */
/* -------------------------------------------------------------------------- */
/** 400 — the change cannot be saved as asked. Each names the cap it hit. */
export const EXTRACTION_FIELD_VALIDATION_CODES = [
    'top_level_refused',
    'invalid_field_key',
    'duplicates_governed_field',
    'description_bounds',
    'description_requests_document_text',
    'description_names_party',
    'description_requests_personal_data',
    /* The same three screens over the key, which is sent in clear too (R-109-9 … R-109-11). */
    'field_key_requests_document_text',
    'field_key_names_party',
    'field_key_requests_personal_data',
    'value_bound_exceeded',
    'field_cap_reached',
    'output_format_over_budget',
    'no_change',
];
/** 404 — an edit, deactivate or reactivate of a key never added. */
export const EXTRACTION_FIELD_NOT_FOUND_CODES = ['field_not_found'];
/** 409 — the registry's state does not admit the change, or moved under the caller. */
export const EXTRACTION_FIELD_CONFLICT_CODES = [
    'field_exists',
    /** An add or edit of a retired key: bring it back with reactivate. */
    'field_retired',
    'field_already_active',
    'field_already_inactive',
    'field_set_changed',
];
/* -------------------------------------------------------------------------- */
/* Shapes                                                                      */
/* -------------------------------------------------------------------------- */
/**
 * The budget the page shows, from the same check the save runs over the
 * current set: the admin-field cap and bounds, and the output format's union
 * count and size against the repo ceiling and Anthropic's limit.
 */
export const ExtractionFieldLimitsSchema = z
    .object({
    active_max: z.number().int().min(1),
    value_max: z.number().int().min(1),
    description_min: z.number().int().min(1),
    description_max: z.number().int().min(1),
    union_count: z.number().int().min(0),
    union_ceiling: z.number().int().min(0),
    union_limit: z.number().int().min(1),
    schema_bytes: z.number().int().min(0),
    schema_bytes_max: z.number().int().min(1),
})
    .strict();
/**
 * One admin field at the revision asked about: its definition in force then,
 * whether it was active, who added it and who last changed it.
 * `definition_revision` is the registry row that defines it.
 */
export const ExtractionFieldEntrySchema = z
    .object({
    field_key: ExtractionFieldKeyWireSchema,
    field_name: FieldNameSchema,
    description: z.string().min(1).max(500),
    value_max: z.number().int().min(1).max(400),
    is_active: z.boolean(),
    added_by_upn: UpnSchema,
    added_utc: IsoTimestampUtcSchema,
    last_change_kind: ExtractionFieldChangeKindSchema,
    last_changed_by_upn: UpnSchema,
    last_changed_utc: IsoTimestampUtcSchema,
    definition_revision: z.number().int().min(1),
})
    .strict()
    .superRefine((entry, ctx) => {
    if (entry.field_name !== `additional_terms.${entry.field_key}`) {
        ctx.addIssue({ code: 'custom', path: ['field_name'], message: 'field_name is additional_terms.<field_key>' });
    }
    if (entry.is_active !== (entry.last_change_kind !== 'deactivate')) {
        ctx.addIssue({ code: 'custom', path: ['is_active'], message: 'a field is inactive exactly when its last change deactivated it' });
    }
});
/** One registry row as the revision history lists it. */
export const ExtractionFieldHistoryEntrySchema = z
    .object({
    field_set_revision: z.number().int().min(1),
    supersedes_revision: z.number().int().min(1).nullable(),
    field_key: ExtractionFieldKeyWireSchema,
    change_kind: ExtractionFieldChangeKindSchema,
    changed_by_upn: UpnSchema,
    changed_utc: IsoTimestampUtcSchema,
    change_note: ExtractionFieldNoteSchema.nullable(),
})
    .strict()
    .superRefine((row, ctx) => {
    const expected = row.field_set_revision === 1 ? null : row.field_set_revision - 1;
    if (row.supersedes_revision !== expected) {
        ctx.addIssue({ code: 'custom', path: ['supersedes_revision'], message: 'each revision supersedes the one before it' });
    }
});
/** The whole row a change wrote — the history entry plus the definition. */
export const ExtractionFieldRowSchema = z
    .object({
    field_set_revision: z.number().int().min(1),
    supersedes_revision: z.number().int().min(1).nullable(),
    field_key: ExtractionFieldKeyWireSchema,
    field_name: FieldNameSchema,
    change_kind: ExtractionFieldChangeKindSchema,
    description: z.string().min(1).max(500),
    value_max: z.number().int().min(1).max(400),
    is_active: z.boolean(),
    changed_by_upn: UpnSchema,
    changed_utc: IsoTimestampUtcSchema,
    change_note: ExtractionFieldNoteSchema.nullable(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* list-extraction-fields                                                      */
/* -------------------------------------------------------------------------- */
/**
 * `as_of_revision` is the DR-09 auditor path: the set as it was at that
 * revision — the one an old audit row names. Omitted, it is the current one.
 */
export const ListExtractionFieldsRequestSchema = z
    .object({
    as_of_revision: Revision.optional(),
})
    .strict();
export const ListExtractionFieldsResponseSchema = z
    .object({
    current_revision: Revision,
    /** The revision `fields` and `history` describe — `current_revision` unless the caller asked for an earlier one. */
    as_of_revision: Revision,
    limits: ExtractionFieldLimitsSchema,
    /** The governed field names, read-only here: changing one needs a release. */
    governed_fields: z.array(FieldNameSchema),
    /** Every key added by `as_of_revision`, active or retired, in key order. */
    fields: z.array(ExtractionFieldEntrySchema),
    /** Every change up to `as_of_revision`, newest first. */
    history: z.array(ExtractionFieldHistoryEntrySchema),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.as_of_revision > body.current_revision) {
        ctx.addIssue({ code: 'custom', path: ['as_of_revision'], message: 'as_of_revision is at most current_revision' });
    }
    if (body.fields.filter((field) => field.is_active).length > body.limits.active_max) {
        ctx.addIssue({ code: 'custom', path: ['fields'], message: 'more active fields than the cap admits' });
    }
    body.fields.forEach((field, index) => {
        if (field.definition_revision > body.as_of_revision) {
            ctx.addIssue({ code: 'custom', path: ['fields', index], message: 'a definition cannot postdate as_of_revision' });
        }
    });
    body.history.forEach((row, index) => {
        if (row.field_set_revision > body.as_of_revision) {
            ctx.addIssue({ code: 'custom', path: ['history', index], message: 'history stops at as_of_revision' });
        }
        const newer = body.history[index - 1];
        if (newer !== undefined && newer.field_set_revision !== row.field_set_revision + 1) {
            ctx.addIssue({ code: 'custom', path: ['history', index], message: 'history is gapless, newest first' });
        }
    });
    if (body.history.length !== body.as_of_revision) {
        ctx.addIssue({ code: 'custom', path: ['history'], message: 'one history row per revision up to as_of_revision' });
    }
});
/* -------------------------------------------------------------------------- */
/* change-extraction-field                                                     */
/* -------------------------------------------------------------------------- */
/** Loose on purpose: the route's checks name the cap (module note). */
const LooseKey = z.string().min(1).max(200);
const LooseDescription = z.string().min(1).max(2_000);
const LooseValueMax = z.number().int().min(1).max(100_000);
export const ExtractionFieldChangeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('add'), field_key: LooseKey, description: LooseDescription, value_max: LooseValueMax, note: ExtractionFieldNoteSchema.optional() }).strict(),
    z.object({ kind: z.literal('edit'), field_key: LooseKey, description: LooseDescription.optional(), value_max: LooseValueMax.optional(), note: ExtractionFieldNoteSchema.optional() }).strict(),
    z.object({ kind: z.literal('deactivate'), field_key: LooseKey, note: ExtractionFieldNoteSchema.optional() }).strict(),
    z.object({ kind: z.literal('reactivate'), field_key: LooseKey, note: ExtractionFieldNoteSchema.optional() }).strict(),
]);
export const ChangeExtractionFieldRequestSchema = z
    .object({
    /** The revision the caller saw. Required: a stale page is a 409, never a silent overwrite. */
    expected_revision: Revision,
    /** Omitted means `list_entry`. `top_level` is refused, naming CLM-80 (FR-33). */
    placement: z.enum(EXTRACTION_FIELD_PLACEMENTS).optional(),
    change: ExtractionFieldChangeSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.change.kind === 'edit' && body.change.description === undefined && body.change.value_max === undefined) {
        ctx.addIssue({ code: 'custom', path: ['change'], message: 'an edit changes the description, the value bound, or both' });
    }
});
/**
 * The new revision, the row it wrote, and the `0046` audit row that records it
 * (CR-07: the admin, the revision before and after, and the time).
 */
export const ChangeExtractionFieldResponseSchema = z
    .object({
    field_set_revision: z.number().int().min(1),
    field: ExtractionFieldRowSchema,
    audit_id: OpaqueIdSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.field.field_set_revision !== body.field_set_revision) {
        ctx.addIssue({ code: 'custom', path: ['field'], message: 'the row written is the revision reported' });
    }
});
//# sourceMappingURL=extraction-fields.js.map